// Pointer, touch, wheel and trackpad input for the canvas.
//
// Apple Pencil always drives the active tool. Fingers pan and pinch; once a pencil has been seen (or pencil-only
// mode is on) a single finger never draws. Two-finger tap = undo, three-finger tap = redo.
//
// Palm rejection: the hand holding the pencil must never move the page. A touch that lands while the pencil is down
// or has just lifted, or that is palm-sized, is ignored until it lifts. Touches still on the glass when the pencil
// lands, that spread to palm size, or that iPadOS cancels were the hand all along: they are dropped too, and the pan
// or pinch they made is undone. While a palm rests or the pencil hovers, a new touch near the hand can only join a
// two- or three-finger tap; one well away from it (the other hand) pans and pinches as usual. Fingers leave the page
// alone until they clearly pan or pinch.
//
// iPadOS now and then loses the end of a touch. Whenever the glass is empty, anything still marked as down is let go,
// so a lost pointerup can't leave the fingers or the pencil stuck.
import type { Editor, PointerInfo, Tool } from "./editor";
import type { Vec } from "./geometry";

interface PointerRec {
  kind: PointerInfo["kind"];
  start: Vec;
  pos: Vec;
  t0: number;
  travel: number;
  /** landed beside a resting palm or under a hovering pencil: may join a multi-finger tap, nothing else */
  tapOnly: boolean;
}

interface CameraState {
  x: number;
  y: number;
  z: number;
}

interface Gesture {
  ids: number[];
  startCam: CameraState;
  startMid: Vec;
  startDist: number;
  last: { p: Vec; t: number };
  vel: Vec;
  /** "wait" until the fingers clearly pan or pinch, so a settling palm or a tap leaves the page alone */
  mode: "wait" | "pan" | "zoom";
}

interface TapCandidate {
  t0: number;
  maxTouches: number;
  travel: number;
  lastInfo: PointerInfo | null;
  /** a tap-only touch joined, so a one-finger tap isn't trusted */
  soft: boolean;
}

/** a pan or pinch that ended this soon before the pencil landed was the hand too (iPadOS may end a palm's touches
 *  and start new ones as the hand settles) */
const LATE_PALM_MS = 350;
/** a touch landing this soon after the pencil lifts is the writing hand */
const PEN_QUIET_MS = 400;
/** one landing this soon after the pencil last hovered over the glass (iPads with Pencil hover) may only tap */
const HOVER_QUIET_MS = 250;
/** a touch this far from every resting palm contact is the other hand, not part of the writing hand */
const PALM_REACH = 200;
/** ...and this far from a hovering pencil tip (the hand holding it spreads further than its palm contacts) */
const HOVER_REACH = 400;
/** a one-finger tap may be held this long (a tap to start typing is often slow) */
const TAP_MS = 500;
/** two- and three-finger taps (undo, redo) must be quick, so a palm landing in two places doesn't undo */
const MULTI_TAP_MS = 320;
/** iPadOS reports a touch's width and height as twice its radius: about 40–65 px for a fingertip, more for a palm */
const PALM_SIZE = 70;
/** a fingertip never spreads this wide, but a hand settling onto the glass does */
const PALM_GROWN = 100;
/** fingers must spread or close this far to start a pinch, */
const PINCH_SLOP = 24;
/** move this far together to start a two-finger pan, or this far alone to start a one-finger pan */
const PAN_SLOP = 14;
const DRAG_SLOP = 10;
/** once panning, the fingers must spread this much further before the zoom changes */
const PAN_TO_PINCH = 56;

const isEditable = (t: EventTarget | null) =>
  t instanceof HTMLElement && (t.isContentEditable || t.tagName === "TEXTAREA" || t.tagName === "INPUT" || !!t.closest("[data-canvas-ignore]"));

export class InputController {
  private rect: DOMRect;
  private pointers = new Map<number, PointerRec>();
  /** touches judged to be the hand holding the pencil, and where they are: ignored until they lift */
  private palms = new Map<number, Vec>();
  private toolPointer: number | null = null;
  private toolKind: PointerInfo["kind"] | null = null;
  private activeTool: Tool | null = null;
  /** the tool pointer's latest position, to finish its stroke if its pointerup never comes */
  private toolInfo: PointerInfo | null = null;
  private gesture: Gesture | null = null;
  /** the camera before the fingers on the glass moved it, so a palm's pan or pinch can be taken back */
  private touchSession: { cam: CameraState; moved: boolean } | null = null;
  /** the last session that moved the camera, and when its fingers lifted */
  private lastSession: { cam: CameraState; endedAt: number } | null = null;
  /** what is moving the camera right now, for the touch log */
  private camSource = "";
  private lastZ: number;
  private penQuietUntil = 0;
  private penHoverAt = -Infinity;
  /** where the pencil tip last was, over or on the glass */
  private penAt: Vec | null = null;
  /** iPadOS lists the pencil among the touches; once seen, a pencil missing from them has lifted */
  private stylusSeen = false;
  private stylusDown = false;
  private staleTimer = 0;
  /** fingers and pencil tips on the glass, counted from touch events */
  private screenTouches = 0;
  private spaceDown = false;
  private mousePan: Vec | null = null;
  private tap: TapCandidate | null = null;
  private glideRaf: number | null = null;
  private safariGesture: { z: number; at: Vec } | null = null;
  private ro: ResizeObserver;
  private cleanup: (() => void)[] = [];

  constructor(private editor: Editor, private el: HTMLElement) {
    this.rect = el.getBoundingClientRect();
    this.ro = new ResizeObserver(() => {
      this.rect = el.getBoundingClientRect();
      editor.camera.resize(this.rect.width, this.rect.height);
    });
    this.ro.observe(el);
    editor.camera.resize(this.rect.width, this.rect.height);
    this.lastZ = editor.camera.z;
    this.cleanup.push(
      editor.camera.subscribe(() => {
        const z = editor.camera.z;
        if (Math.abs(z / this.lastZ - 1) < 0.002) return;
        this.lastZ = z;
        editor.inputLog.zoomed(z, this.camSource || "other");
      })
    );
    this.listen(el, "pointerdown", this.onDown as EventListener);
    this.listen(el, "pointermove", this.onMove as EventListener);
    this.listen(el, "pointerup", this.onUp as EventListener);
    this.listen(el, "pointercancel", this.onCancel as EventListener);
    this.listen(el, "pointerleave", this.onLeave as EventListener);
    this.listen(el, "wheel", this.onWheel as EventListener, { passive: false });
    this.listen(el, "touchstart", this.blockTouch as EventListener, { passive: false });
    this.listen(el, "touchmove", this.blockTouch as EventListener, { passive: false });
    this.listen(el, "gesturestart", this.onSafariGestureStart as EventListener, { passive: false });
    this.listen(el, "gesturechange", this.onSafariGestureChange as EventListener, { passive: false });
    this.listen(el, "gestureend", this.onSafariGestureEnd as EventListener, { passive: false });
    this.listen(el, "contextmenu", this.onContextMenu as EventListener);
    // what's on the glass: Safari's own pinch events must not zoom while touches are down
    for (const type of ["touchstart", "touchend", "touchcancel"]) {
      this.listen(window, type, this.countTouches as EventListener, { capture: true, passive: true });
    }
    // and Safari must not zoom the whole page when a palm or two fingers pinch on the toolbars
    this.listen(document, "gesturestart", this.stopPageZoom, { passive: false });
    this.listen(document, "gesturechange", this.stopPageZoom, { passive: false });
    this.listen(document, "visibilitychange", this.onVisibility);
    this.listen(window, "keydown", this.onKeyDown as EventListener);
    this.listen(window, "keyup", this.onKeyUp as EventListener);
    this.listen(window, "blur", this.onBlur as EventListener);
    this.listen(window, "scroll", this.refreshRect as EventListener, { passive: true });
  }

  destroy(): void {
    this.ro.disconnect();
    for (const c of this.cleanup) c();
    if (this.glideRaf !== null) cancelAnimationFrame(this.glideRaf);
    window.clearTimeout(this.staleTimer);
  }

  private listen(t: EventTarget, type: string, fn: EventListener, opts?: AddEventListenerOptions): void {
    const bound = fn.bind(this);
    t.addEventListener(type, bound, opts);
    this.cleanup.push(() => t.removeEventListener(type, bound, opts));
  }

  private refreshRect(): void {
    this.rect = this.el.getBoundingClientRect();
  }

  private info(e: PointerEvent, kind?: PointerInfo["kind"]): PointerInfo {
    const k = kind ?? (e.pointerType === "pen" ? "pen" : e.pointerType === "touch" ? "touch" : "mouse");
    const screen = { x: e.clientX - this.rect.left, y: e.clientY - this.rect.top };
    return {
      id: e.pointerId,
      kind: k,
      screen,
      world: this.editor.camera.toWorld(screen),
      pressure: k === "pen" ? Math.max(0.03, e.pressure || 0.5) : 0.5,
      hasPressure: k === "pen",
      shift: e.shiftKey,
      alt: e.altKey,
      meta: e.metaKey || e.ctrlKey,
      button: e.button,
      time: e.timeStamp || performance.now(),
      eraserButton: k === "pen" && (e.button === 5 || (e.buttons & 32) === 32)
    };
  }

  private blockTouch(e: TouchEvent): void {
    // stop iPadOS scrolling, zooming, the magnifier and Scribble on the canvas (but not in text fields)
    if (!isEditable(e.target)) e.preventDefault();
  }

  private countTouches(e: TouchEvent): void {
    this.screenTouches = e.touches.length;
    let stylus = false;
    for (let i = 0; i < e.touches.length; i++) if ((e.touches[i] as Touch & { touchType?: string }).touchType === "stylus") stylus = true;
    if (stylus) this.stylusSeen = true;
    this.stylusDown = stylus;
    this.editor.inputLog.add({ t: e.timeStamp || performance.now(), e: "touch", k: e.type, n: e.touches.length });
    // nothing on the glass, so no palm is still resting, even if its pointerup went astray
    if (!e.touches.length) this.palms.clear();
    // pointer events come just before their touch events; whatever is still down a moment later lost its pointerup
    if (e.type !== "touchstart" && (!e.touches.length || (this.stylusSeen && !stylus))) {
      window.clearTimeout(this.staleTimer);
      this.staleTimer = window.setTimeout(() => this.dropStale(), 60);
    }
  }

  /** Let go of fingers, and the pencil, that are still marked as down though the glass says they lifted. */
  private dropStale(): void {
    if (!this.screenTouches) {
      const stale = this.touchIds(true);
      if (stale.length) {
        this.editor.inputLog.add({ t: performance.now(), e: "stale", k: "touch", n: stale.length });
        for (const id of stale) {
          const rec = this.pointers.get(id)!;
          this.pointers.delete(id);
          if (!rec.tapOnly) this.liftFinger(id, this.toolInfo && id === this.toolPointer ? this.toolInfo : this.tapInfo(rec));
        }
        // a lost touch is no tap
        this.touchSession = null;
        this.gesture = null;
        this.tap = null;
      }
    }
    if (this.toolKind === "pen" && this.stylusSeen && !this.stylusDown) {
      this.editor.inputLog.add({ t: performance.now(), e: "stale", k: "pen" });
      this.finishTool(false);
    }
  }

  /** A pointer record as tool input, for a finger whose pointerup never came. */
  private tapInfo(rec: PointerRec): PointerInfo {
    const ed = this.editor;
    return { id: -1, kind: rec.kind, screen: rec.pos, world: ed.camera.toWorld(rec.pos), pressure: 0.5, hasPressure: false, shift: false, alt: false, meta: false, button: 0, time: performance.now(), eraserButton: false };
  }

  /** The tool's pointer lifted (or was lost): finish or drop its stroke. */
  private finishTool(cancelled: boolean, info: PointerInfo | null = this.toolInfo): void {
    const tool = this.activeTool;
    const kind = this.toolKind;
    this.toolPointer = null;
    this.activeTool = null;
    this.toolKind = null;
    this.toolInfo = null;
    if (cancelled || !info) tool?.onCancel();
    else tool?.onUp(info);
    if (kind === "pen") this.penQuietUntil = performance.now() + PEN_QUIET_MS;
  }

  /** Is this spot within reach of the hand holding the pencil (a resting palm, or a hovering pencil)? */
  private nearHand(p: Vec, now: number): string {
    for (const q of this.palms.values()) if (Math.hypot(p.x - q.x, p.y - q.y) < PALM_REACH) return "palm resting";
    if (now - this.penHoverAt < HOVER_QUIET_MS && this.penAt && Math.hypot(p.x - this.penAt.x, p.y - this.penAt.y) < HOVER_REACH) return "pen hovering";
    return "";
  }

  private stopPageZoom(e: Event): void {
    e.preventDefault();
  }

  // ---------- pointer events ----------

  private onDown(e: PointerEvent): void {
    if (isEditable(e.target)) return;
    const ed = this.editor;
    const kind = e.pointerType === "pen" ? "pen" : e.pointerType === "touch" ? "touch" : "mouse";
    const now = performance.now();
    const log = ed.inputLog;
    const at = { x: e.clientX - this.rect.left, y: e.clientY - this.rect.top };
    log.pointer("down", e, at.x, at.y);

    if (kind === "touch") {
      const palm = this.toolKind === "pen" ? "pen down" : now < this.penQuietUntil ? "pen just lifted" : e.width > PALM_SIZE && e.height > PALM_SIZE ? "palm-sized" : "";
      if (palm) {
        log.add({ t: now, e: "palm", k: palm, id: e.pointerId });
        this.palms.set(e.pointerId, at);
        return;
      }
    }
    const near = kind === "touch" ? this.nearHand(at, now) : "";
    const tapOnly = !!near;
    if (tapOnly) log.add({ t: now, e: "tapOnly", k: near, id: e.pointerId });
    if (!tapOnly) {
      this.stopGlide();
      ed.camera.stop();
    }
    if (kind === "pen") {
      this.penAt = at;
      if (!ed.settings.penSeen) ed.setSettings({ penSeen: true });
      log.penDown();
      // whatever is on the glass as the pencil lands is the hand holding it
      if (!this.rejectTouches("pen landed")) {
        const last = this.lastSession;
        if (last && now - last.endedAt < LATE_PALM_MS) this.setCamera(last.cam, "late palm revert");
      }
      this.lastSession = null;
    }

    try {
      this.el.setPointerCapture(e.pointerId);
    } catch {
      /* pointer already gone */
    }
    const info = this.info(e, kind);
    this.pointers.set(e.pointerId, { kind, start: info.screen, pos: info.screen, t0: now, travel: 0, tapOnly });

    if (kind === "touch") {
      const all = this.touchIds(true);
      if (!this.tap || all.length === 1) this.tap = { t0: now, maxTouches: 0, travel: 0, lastInfo: null, soft: false };
      this.tap.maxTouches = Math.max(this.tap.maxTouches, all.length);
      this.tap.lastInfo = info;
      if (tapOnly) {
        this.tap.soft = true;
        return;
      }
      const cam = ed.camera;
      this.touchSession ??= { cam: { x: cam.x, y: cam.y, z: cam.z }, moved: false };
      const touches = this.touchIds();
      if (touches.length === 1) {
        if (ed.fingersNavigate || ed.ui.tool === "hand" || ed.ui.readOnly) this.startGesture([e.pointerId]);
        else this.startTool(info, e);
      } else {
        if (this.toolKind === "touch") {
          // a second finger turns a one-finger stroke into a pinch
          this.activeTool?.onCancel();
          this.toolPointer = null;
          this.toolKind = null;
          this.activeTool = null;
        }
        this.startGesture(touches.slice(0, 2));
      }
      return;
    }

    if (kind === "pen") {
      this.tap = null;
      // a stroke still open here lost its pointerup (writing fast): keep it rather than throw it away
      if (this.toolPointer !== null) this.finishTool(this.toolKind !== "pen");
      this.startTool(info, e);
      return;
    }

    // mouse
    if (e.button === 1 || (e.button === 0 && this.spaceDown)) {
      this.mousePan = info.screen;
      this.el.style.cursor = "grabbing";
      return;
    }
    if (e.button === 0) this.startTool(info, e);
  }

  private startTool(info: PointerInfo, e: PointerEvent): void {
    const ed = this.editor;
    let tool = ed.tool;
    if (info.eraserButton) tool = ed.tools.get("eraser")!;
    else if (info.kind === "pen" && ed.ui.readOnly && tool.id !== "laser") tool = ed.tools.get("hand")!;
    // writing with the keyboard: fingers type, but the pencil still writes
    else if (info.kind === "pen" && ed.keyboardMode) tool = ed.tools.get("pen")!;
    this.toolPointer = e.pointerId;
    this.toolKind = info.kind;
    this.activeTool = tool;
    this.toolInfo = info;
    tool.onDown(info);
  }

  /** Fingers on the canvas, optionally with the tap-only touches too. */
  private touchIds(withTapOnly = false): number[] {
    return [...this.pointers.entries()].filter(([, r]) => r.kind === "touch" && (withTapOnly || !r.tapOnly)).map(([id]) => id);
  }

  /** The touches on the glass are the hand holding the pencil: ignore them until they lift, and undo the pan or
   *  pinch they made. */
  private rejectTouches(why: string): boolean {
    const ids = this.touchIds(true);
    for (const id of ids) {
      this.palms.set(id, this.pointers.get(id)!.pos);
      this.pointers.delete(id);
    }
    if (this.toolKind === "touch") this.finishTool(true);
    this.gesture = null;
    this.tap = null;
    const session = this.touchSession;
    this.touchSession = null;
    if (ids.length) this.editor.inputLog.add({ t: performance.now(), e: "reject", k: why, n: session?.moved ? 1 : 0 });
    if (!session?.moved) return false;
    this.stopGlide();
    this.setCamera(session.cam, "palm revert");
    return true;
  }

  private setCamera(c: CameraState, source: string): void {
    this.camSource = source;
    this.editor.camera.set(c.x, c.y, c.z);
    this.camSource = "";
  }

  private onMove(e: PointerEvent): void {
    const rec = this.pointers.get(e.pointerId);
    this.editor.inputLog.pointer("move", e, e.clientX - this.rect.left, e.clientY - this.rect.top);
    const palm = this.palms.get(e.pointerId);
    if (palm) {
      palm.x = e.clientX - this.rect.left;
      palm.y = e.clientY - this.rect.top;
      return;
    }
    if (rec?.kind === "touch" && e.width > PALM_GROWN && e.height > PALM_GROWN) {
      this.rejectTouches("grew to palm size"); // a contact that spreads this wide is a hand settling onto the glass
      return;
    }
    const info = this.info(e);
    if (rec) {
      rec.travel += Math.hypot(info.screen.x - rec.pos.x, info.screen.y - rec.pos.y);
      rec.pos = info.screen;
      if (this.tap && rec.kind === "touch") this.tap.travel = Math.max(this.tap.travel, rec.travel);
    }
    if (info.kind === "pen") this.penAt = info.screen;
    if (e.pointerId === this.toolPointer && this.activeTool) {
      this.toolInfo = info;
      const raw = typeof e.getCoalescedEvents === "function" ? e.getCoalescedEvents() : [];
      const coalesced = raw.length ? raw.map((c) => this.info(c, info.kind)) : [info];
      const predictedRaw = typeof e.getPredictedEvents === "function" ? e.getPredictedEvents() : [];
      const predicted = predictedRaw.map((c) => this.info(c, info.kind));
      this.activeTool.onMove(info, coalesced, predicted);
      return;
    }
    if (this.gesture && this.gesture.ids.includes(e.pointerId)) {
      this.updateGesture();
      return;
    }
    if (this.mousePan && info.kind === "mouse") {
      this.editor.camera.panBy(info.screen.x - this.mousePan.x, info.screen.y - this.mousePan.y);
      this.mousePan = info.screen;
      return;
    }
    if (!rec && e.buttons === 0 && (info.kind === "mouse" || info.kind === "pen")) {
      if (info.kind === "pen") this.penHoverAt = performance.now();
      const tool = this.editor.tool;
      tool.onHover?.(info);
      if (info.kind === "mouse" && tool.id !== "select") this.el.style.cursor = tool.cursor();
    }
  }

  private onUp(e: PointerEvent): void {
    this.finishPointer(e, false);
  }

  private onCancel(e: PointerEvent): void {
    this.finishPointer(e, true);
  }

  private onLeave(e: PointerEvent): void {
    if (e.pointerType === "pen" || e.pointerType === "mouse") {
      if (!this.pointers.has(e.pointerId)) this.editor.tool.onHover?.(null);
    }
  }

  private finishPointer(e: PointerEvent, cancelled: boolean): void {
    this.editor.inputLog.pointer(cancelled ? "cancel" : "up", e, e.clientX - this.rect.left, e.clientY - this.rect.top);
    if (this.palms.delete(e.pointerId)) return;
    const rec = this.pointers.get(e.pointerId);
    if (cancelled && rec?.kind === "touch") {
      // iPadOS cancels a touch it takes for a resting palm: the rest of the hand goes too, and so does its pan or
      // pinch
      this.rejectTouches("cancelled by the system");
      this.palms.delete(e.pointerId);
      return;
    }
    this.pointers.delete(e.pointerId);
    const info = this.info(e);
    const now = performance.now();
    if (rec?.kind === "touch") {
      if (!this.touchIds().length) {
        if (this.touchSession?.moved) this.lastSession = { cam: this.touchSession.cam, endedAt: now };
        this.touchSession = null;
      }
      if (!rec.tapOnly) this.liftFinger(e.pointerId, info);
      if (!this.touchIds(true).length) this.finishTap(now);
      return;
    }

    if (e.pointerId === this.toolPointer) {
      this.finishTool(cancelled, info);
      return;
    }

    if (this.mousePan && info.kind === "mouse") {
      this.mousePan = null;
      this.el.style.cursor = "";
    }
  }

  /** A finger lifted: finish its stroke, or carry the pan or pinch on with the fingers that are left. */
  private liftFinger(id: number, info: PointerInfo): void {
    if (id === this.toolPointer) {
      this.finishTool(false, info);
      if (this.tap && this.tap.maxTouches === 1) this.tap = null;
      return;
    }
    if (!this.gesture?.ids.includes(id)) return;
    const remaining = this.touchIds();
    if (remaining.length >= 2) this.startGesture(remaining.slice(0, 2));
    else if (remaining.length === 1 && (this.editor.fingersNavigate || this.editor.ui.tool === "hand" || this.editor.ui.readOnly)) this.startGesture(remaining);
    else this.endGesture(remaining.length === 0);
  }

  /** All fingers lifted: was it a quick tap? */
  private finishTap(now: number): void {
    const tap = this.tap;
    this.tap = null;
    if (!tap || now - tap.t0 > (tap.maxTouches > 1 ? MULTI_TAP_MS : TAP_MS) || tap.travel > 14) return;
    const ed = this.editor;
    if (tap.maxTouches === 2 && ed.settings.fingerUndo) ed.undo();
    else if (tap.maxTouches === 3 && ed.settings.fingerUndo) ed.redo();
    else if (tap.maxTouches === 1 && tap.lastInfo && !tap.soft) {
      const tool = ed.tool;
      const info = { ...tap.lastInfo, world: ed.camera.toWorld(tap.lastInfo.screen) };
      if (tool.onTap) tool.onTap(info);
      else ed.tools.get("select")?.onTap?.(info);
    }
  }

  // ---------- pinch / pan ----------

  private startGesture(ids: number[]): void {
    const cam = this.editor.camera;
    const pts = ids.map((id) => this.pointers.get(id)!.pos);
    const mid = pts.length === 2 ? { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 } : pts[0];
    const d = pts.length === 2 ? Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) : 0;
    this.gesture = { ids, startCam: { x: cam.x, y: cam.y, z: cam.z }, startMid: mid, startDist: d, last: { p: mid, t: performance.now() }, vel: { x: 0, y: 0 }, mode: "wait" };
  }

  private updateGesture(): void {
    const g = this.gesture!;
    const pts = g.ids.map((id) => this.pointers.get(id)?.pos).filter(Boolean) as Vec[];
    if (pts.length !== g.ids.length) return;
    const two = pts.length === 2;
    const mid = two ? { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 } : pts[0];
    const d = two ? Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) : 0;
    if (g.mode !== "zoom") {
      // A pan has to move clearly and a pinch has to spread clearly, and a pan only turns into a pinch after a
      // big spread, so the small shifts of a settling palm do nothing.
      const spread = Math.abs(d - g.startDist);
      const drift = Math.hypot(mid.x - g.startMid.x, mid.y - g.startMid.y);
      let next: Gesture["mode"] | null = null;
      if (two && spread >= (g.mode === "pan" ? PAN_TO_PINCH : PINCH_SLOP)) next = "zoom";
      else if (g.mode === "wait" && drift >= (two ? PAN_SLOP : DRAG_SLOP)) next = "pan";
      if (next) {
        // move from here on, so the slop doesn't jump the page
        const cam = this.editor.camera;
        g.mode = next;
        g.startMid = mid;
        g.startDist = d;
        g.startCam = { x: cam.x, y: cam.y, z: cam.z };
        g.last = { p: mid, t: performance.now() };
        this.editor.inputLog.add({ t: performance.now(), e: "mode", k: next, n: g.ids.length });
        return;
      }
      if (g.mode === "wait") return;
    }
    let z = g.startCam.z;
    if (g.mode === "zoom" && g.startDist > 10) z = g.startCam.z * (d / g.startDist);
    z = Math.min(24, Math.max(0.04, z));
    const wx = (g.startMid.x - g.startCam.x) / g.startCam.z;
    const wy = (g.startMid.y - g.startCam.y) / g.startCam.z;
    this.camSource = "fingers";
    this.editor.camera.set(mid.x - wx * z, mid.y - wy * z, z);
    this.camSource = "";
    if (this.touchSession) this.touchSession.moved = true;
    const now = performance.now();
    const dt = Math.max(1, now - g.last.t);
    g.vel = { x: ((mid.x - g.last.p.x) / dt) * 0.7 + g.vel.x * 0.3, y: ((mid.y - g.last.p.y) / dt) * 0.7 + g.vel.y * 0.3 };
    g.last = { p: mid, t: now };
  }

  private endGesture(allowGlide: boolean): void {
    const g = this.gesture;
    this.gesture = null;
    if (!g || !allowGlide) return;
    if (performance.now() - g.last.t > 80) return;
    let v = { ...g.vel };
    if (Math.hypot(v.x, v.y) < 0.15) return;
    let t = performance.now();
    const step = () => {
      const now = performance.now();
      const dt = now - t;
      t = now;
      v = { x: v.x * Math.pow(0.993, dt), y: v.y * Math.pow(0.993, dt) };
      if (Math.hypot(v.x, v.y) < 0.02) {
        this.glideRaf = null;
        return;
      }
      this.editor.camera.panBy(v.x * dt, v.y * dt);
      this.glideRaf = requestAnimationFrame(step);
    };
    this.glideRaf = requestAnimationFrame(step);
  }

  private stopGlide(): void {
    if (this.glideRaf !== null) cancelAnimationFrame(this.glideRaf);
    this.glideRaf = null;
  }

  // ---------- wheel & trackpad ----------

  private onWheel(e: WheelEvent): void {
    if (isEditable(e.target)) return;
    e.preventDefault();
    this.stopGlide();
    const cam = this.editor.camera;
    const at = { x: e.clientX - this.rect.left, y: e.clientY - this.rect.top };
    const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? cam.height : 1;
    if (e.ctrlKey || e.metaKey) {
      const factor = Math.exp(-e.deltaY * unit * 0.0105);
      this.camSource = "wheel";
      cam.zoomAt(at, Math.min(1.35, Math.max(0.74, factor)));
      this.camSource = "";
    } else {
      let dx = e.deltaX * unit;
      let dy = e.deltaY * unit;
      if (e.shiftKey && !dx) {
        dx = dy;
        dy = 0;
      }
      cam.panBy(-dx, -dy);
    }
  }

  private onSafariGestureStart(e: Event & { scale?: number; clientX?: number; clientY?: number }): void {
    e.preventDefault();
    this.editor.inputLog.add({ t: performance.now(), e: "gesture", k: "start", n: e.scale ?? 1 });
    // Touches pinch through pointer events, and Safari also reports a resting palm as a pinch, so this path is
    // only for a Mac trackpad: never on a touch screen.
    if (this.screenTouches || navigator.maxTouchPoints > 0) return;
    const at = { x: (e.clientX ?? this.rect.width / 2) - this.rect.left, y: (e.clientY ?? this.rect.height / 2) - this.rect.top };
    this.safariGesture = { z: this.editor.camera.z, at };
  }

  private onSafariGestureChange(e: Event & { scale?: number }): void {
    e.preventDefault();
    this.editor.inputLog.add({ t: performance.now(), e: "gesture", k: "change", n: e.scale ?? 1 });
    if (!this.safariGesture || this.screenTouches || this.pointers.size) return;
    const cam = this.editor.camera;
    const target = this.safariGesture.z * (e.scale ?? 1);
    this.camSource = "safari pinch";
    cam.zoomAt(this.safariGesture.at, target / cam.z);
    this.camSource = "";
  }

  private onSafariGestureEnd(e: Event): void {
    e.preventDefault();
    this.safariGesture = null;
  }

  private onContextMenu(e: MouseEvent): void {
    if (isEditable(e.target)) return;
    e.preventDefault();
    const at = { x: e.clientX - this.rect.left, y: e.clientY - this.rect.top };
    const ed = this.editor;
    const hit = ed.hitAt(ed.camera.toWorld(at), 6, { includeLocked: true });
    if (hit && !ed.ui.selection.includes(hit.id)) ed.select([hit.id]);
    this.el.dispatchEvent(new CustomEvent("sepia:contextmenu", { detail: at }));
  }

  // ---------- keyboard (space-to-pan only; shortcuts live in shortcuts.ts) ----------

  private onKeyDown(e: KeyboardEvent): void {
    if (e.code === "Space" && !isEditable(e.target) && !this.spaceDown) {
      this.spaceDown = true;
      this.el.style.cursor = "grab";
    }
  }

  private onKeyUp(e: KeyboardEvent): void {
    if (e.code === "Space") {
      this.spaceDown = false;
      this.el.style.cursor = "";
    }
  }

  private onBlur(): void {
    this.spaceDown = false;
    if (this.toolPointer !== null) this.finishTool(true);
    this.gesture = null;
    this.pointers.clear();
    this.palms.clear();
    this.touchSession = null;
    this.lastSession = null;
    this.tap = null;
    this.screenTouches = 0;
  }

  private onVisibility(): void {
    if (document.visibilityState === "hidden") this.onBlur();
  }
}
